import axios from "axios";

// Growing Circle uses the unified first-party API path served by the
// canonical selfdoubling.com origin. Since 15 Sep 2026 the Circle
// FastAPI routes are mounted IN-PROCESS inside the website backend
// (port 8001) under the prefix "/api/growing-circle/*" — there is no
// separate Circle process on port 8002 and no httpx reverse proxy.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/growing-circle`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// --------------------------- Auto-refresh interceptor ---------------------------
// When the short-lived access_token cookie expires (or is evicted by the
// browser earlier than its Max-Age — e.g. Partitioned cookies dropped by
// Cloudflare edge policies), the next call comes back with HTTP 401.
// Rather than bouncing the user to /login, we transparently call
// /auth/refresh (which uses the long-lived refresh_token cookie) and
// replay the original request exactly once. Only if the refresh itself
// fails do we surface the 401 to the caller.
let refreshInFlight = null;
async function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// --------------------------- Two-Step Acceptance interceptor ---------------------------
// Whenever the server signals that the current user has not accepted the
// current legal versions (§29) or has just submitted an outdated version
// (§26), we broadcast a browser event so the AcceptanceProvider re-fetches
// the authoritative status and re-mounts the gate.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.detail?.code;
    const original = error?.config;
    if ((status === 403 && code === "acceptance_required") ||
        (status === 409 && code === "terms_version_changed")) {
      try { window.dispatchEvent(new CustomEvent("gc:acceptance-invalidated", { detail: { status, code } })); } catch {}
    }
    // Auto-refresh: retry ONCE on 401 if we haven't already tried on this
    // request. Skip auth endpoints themselves to avoid infinite loops.
    const url = (original && original.url) || "";
    const isAuthEndpoint = /\/auth\/(login|refresh|logout|forgot-password|reset-password)/.test(url);
    if (status === 401 && original && !original.__gcRetried && !isAuthEndpoint) {
      original.__gcRetried = true;
      try {
        await refreshOnce();
        return api.request(original);
      } catch (_) {
        // Refresh failed — propagate the original 401 unchanged.
      }
    }
    return Promise.reject(error);
  }
);

// Human-readable Italian mapping for server error codes emitted by the
// Circle backend as HTTPException(detail={"code": ..., "message": ...}).
const ERROR_CODE_IT = {
  invalid_body: "Il contenuto dell'articolo contiene elementi non ammessi.",
  slug_taken: "Esiste già un articolo con lo stesso slug: cambia il titolo o lo slug.",
  acceptance_required: "Devi accettare i termini prima di procedere.",
  terms_version_changed: "I termini sono stati aggiornati: rileggili e riaccettali.",
  not_found: "Elemento non trovato.",
  forbidden: "Non hai i permessi per questa operazione.",
  rate_limited: "Troppi tentativi. Riprova tra qualche minuto.",
  too_large: "Immagine troppo grande (max 8 MB). Riducila e riprova.",
  invalid_image: "File non valido: non è un'immagine leggibile.",
  unsupported_format: "Formato non supportato. Usa JPG, PNG o WEBP (le foto HEIC di iPhone vanno convertite).",
  storage_unavailable: "Archiviazione temporaneamente non disponibile. Riprova tra poco.",
};

// Italian labels for the fields that appear in Pydantic validation errors.
const FIELD_LABELS_IT = {
  title: "Titolo",
  subtitle: "Sottotitolo",
  series_name: "Serie",
  author_name: "Nome autore",
  author_credentials: "Credenziali autore",
  opening_note: "Nota di apertura",
  body: "Corpo articolo",
  author_note_or_biography: "Biografia",
  copyright_note: "Nota di copyright",
  cover_media_id: "Copertina",
  cover_alt_text: "Testo alternativo copertina",
  slug: "URL articolo",
  publish_at: "Data di pubblicazione",
  email: "Email",
  password: "Password",
  name: "Nome",
};

// Map a single Pydantic validation error object to a friendly Italian
// sentence. Falls back to Pydantic's own English `msg` if the error type
// is unknown so no information is lost.
function formatPydanticError(e) {
  if (!e || typeof e !== "object") return typeof e === "string" ? e : JSON.stringify(e);
  const loc = Array.isArray(e.loc) ? e.loc : [];
  // Skip the leading "body" segment that FastAPI adds.
  const fieldPath = loc.filter((x, i) => !(i === 0 && x === "body"));
  const lastField = fieldPath[fieldPath.length - 1];
  const label = FIELD_LABELS_IT[lastField] || (lastField ? String(lastField) : "Campo");
  switch (e.type) {
    case "missing":
      return `${label}: campo obbligatorio.`;
    case "string_too_short": {
      const min = e.ctx && e.ctx.min_length;
      return min && min > 1
        ? `${label}: minimo ${min} caratteri.`
        : `${label}: non può essere vuoto.`;
    }
    case "string_too_long": {
      const max = e.ctx && e.ctx.max_length;
      return `${label}: massimo ${max || "consentito"} caratteri.`;
    }
    case "value_error":
    case "assertion_error":
      return `${label}: ${e.msg || "valore non valido"}.`;
    case "type_error":
      return `${label}: tipo di dato non valido.`;
    default:
      return e.msg ? `${label}: ${e.msg}` : `${label}: valore non valido.`;
  }
}

export function formatApiError(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(formatPydanticError).filter(Boolean).join(" ");
  if (typeof detail === "object") {
    // Pydantic single validation-error object: {msg, type, loc, ctx}
    if (typeof detail.type === "string" && (detail.msg || detail.loc)) {
      return formatPydanticError(detail);
    }
    if (typeof detail.msg === "string") return detail.msg;
    // Backend HTTPException object: {code, message, ...}
    if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
    if (typeof detail.code === "string") {
      const mapped = ERROR_CODE_IT[detail.code];
      if (mapped) return mapped;
      return `Errore: ${detail.code}`;
    }
    try { return JSON.stringify(detail); } catch { return "Errore sconosciuto."; }
  }
  return String(detail);
}

export default api;
